/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { state } from '@/quote-management/lib/state';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';
import { REMOVE_NEGOTIABLE_QUOTE_ITEMS_MUTATION } from './graphql/RemoveNegotiableQuoteItemsMutation';

export interface RemoveNegotiableQuoteItemsInput {
  quoteUid: string;
  quoteItemUids: string[];
}

export const removeNegotiableQuoteItems = async (
  input: RemoveNegotiableQuoteItemsInput
): Promise<NegotiableQuoteModel | null> => {
  if (!state.authenticated) {
    return Promise.reject(new Error('Unauthorized'));
  }

  const { quoteUid, quoteItemUids } = input;

  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  if (!Array.isArray(quoteItemUids) || quoteItemUids.length === 0) {
    throw new Error('At least one quote item UID is required');
  }

  const response = await fetchGraphQl(REMOVE_NEGOTIABLE_QUOTE_ITEMS_MUTATION, {
    variables: {
      quoteUid,
      quoteItemUids,
    },
  });

  const { errors, data } = response;

  if (errors?.length) {
    const errorMessages = errors.map((error) => error.message).join('; ');
    throw new Error(
      `Failed to remove negotiable quote items: ${errorMessages}`
    );
  }

  const quote = transformQuote(data?.removeNegotiableQuoteItems?.quote);

  if (!quote) {
    throw new Error(
      'Failed to transform quote data: Invalid response structure'
    );
  }

  events.emit('quote-management/quote-items-removed', {
    quote,
    removedItemUids: quoteItemUids,
    input,
  });

  return quote;
};
